import React, { useContext, useMemo } from 'react'
import cn from 'classnames'
import {
  Auth,
  GoogleSocialStartFlowButton,
  useGoogleOAuthRedirect,
  NAV_PATHNAME_STATE_KEY,
  NAV_QUERY_STATE_KEY,
} from '@grammarly/identity-ui'

import { useExperimentService } from '../../../../lib/hooks'
import { navHelper } from '../../../../lib/services/navigation'
import { trackGA4BtnClick } from '../../../../lib/tracking/google-tag-manager/events'
import { waitGnar } from '../../../../lib/utils/promise'
import { authConfig } from '../../../unified-auth/config'
import { UnifiedAuthTracker } from '../../../unified-auth/tracking'
import { PageContext } from '../../../../contexts/page-context'
import { usePlacementContext } from '../../../../contexts/placement-context'
import { useSignUpProductRecUrl } from '../signup-product-recommendation/hooks/useSignupAction'

import styles from './signup-google.module.scss'
import '@grammarly/identity-ui/dist/prebuilt/esm/index.css'
import { ComponentCtaButtonSize } from '../../../../lib/types'
import { ButtonColor, buttonColorToClass } from '../../../ui-lib/button'
import { CtaToQueryParam } from '../types'

const authAction: Auth.ActionType = 'SIGNUP'

const googleAuthContext: Auth.SocialContext = {
  action: authAction,
  socialType: Auth.Social.Type.Google,
}

const formKey = Auth.Tracking.FormType.SignUpStart

interface GoogleSocialButtonProps {
  unifiedAuthTracker: UnifiedAuthTracker
  text: React.ReactNode
  toQueryParam?: CtaToQueryParam
  isPrimaryColor?: boolean
  className?: string
}

const GoogleButton = (props: GoogleSocialButtonProps) => {
  const { placement } = usePlacementContext()
  const { queryParams } = useSignUpProductRecUrl({ toQueryParam: props.toQueryParam })

  const onBeforeSocialRedirect = (_: Auth.SocialContext, appendData: <T>(key: string, data: T) => void) => {
    appendData(NAV_QUERY_STATE_KEY, { ...navHelper.currentQueryParams, ...queryParams })
    appendData(NAV_PATHNAME_STATE_KEY, '/signup')
  }

  const { getSocialRedirectUrl } = useGoogleOAuthRedirect({
    cookieDomain: authConfig.cookieDomain,
    oAuthConfig: authConfig.socialOAuth.google,
    authAction,
    formKey,
    onBeforeSocialRedirect,
  })

  const onClick = async () => {
    trackGA4BtnClick(placement)
    await waitGnar(props.unifiedAuthTracker.trackSubmitButtonClick(googleAuthContext, formKey))

    const socialRedirectUrl = getSocialRedirectUrl()
    window.location.assign(socialRedirectUrl)
  }

  return (
    <GoogleSocialStartFlowButton
      className={cn(props.className, styles.googleButton24)}
      authAction={authAction}
      text={props.text}
      onClick={onClick}
      isPrimaryColor={props.isPrimaryColor}
    />
  )
}

interface SignUpGoogleButtonProps {
  text: React.ReactNode
  className?: string
  size?: string
  color?: ButtonColor
  toQueryParam?: CtaToQueryParam
}

export const SignUpGoogleButton = (props: SignUpGoogleButtonProps) => {
  const { text, className, size, color, toQueryParam } = props
  const { isGDPR } = useExperimentService()
  const { pageId, slug } = useContext(PageContext)

  const unifiedAuthTracker: UnifiedAuthTracker = useMemo(
    () => new UnifiedAuthTracker(pageId, slug, isGDPR),
    [pageId, slug, isGDPR]
  )

  return (
    <GoogleButton
      className={cn(
        className,
        size === ComponentCtaButtonSize.Large && styles.googleButtonLarge,
        color === ButtonColor.WDK24GoogleSecondaryDarkBg ? buttonColorToClass(color, true) : styles.googleButtonLight24
      )}
      unifiedAuthTracker={unifiedAuthTracker}
      text={text}
      toQueryParam={toQueryParam}
    />
  )
}
